

import { useState, useEffect, useRef, useCallback } from "react"
import "katex/dist/katex.min.css"
import { Helmet } from "react-helmet"
import AIResponse from "./AiResponse"
import { FaHistory, FaTimes, FaImage, FaTimes as FaClose, FaBrain, FaPaperPlane, FaTrash, FaPlus } from "react-icons/fa"
import { useLocation } from "react-router-dom"
import "./chatbox.css"
import EducationalToolbar from "./chatboxComponents/EducationalToolbar"
import ReactDOM from "react-dom/client"
import PracticeDisplay from "./chatboxComponents/PracticeDisplay"
import { motion, AnimatePresence } from "framer-motion";
import FileUploadHandler from "./chatboxComponents/FileUploadHandler"
import { processFiles } from "./utils/fileProcessor"

// Add Mermaid.js library
import mermaid from "mermaid"

// Initialize mermaid
mermaid.initialize({
  startOnLoad: true,
  theme: "default",
  securityLevel: "loose",
  fontFamily: "Segoe UI, sans-serif",
})

// Add these IndexedDB utility functions
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open("ChatHistoryDB", 1)

    request.onerror = (event) => {
      console.error("Error opening IndexedDB:", event.target.error)
      reject(event.target.error)
    }

    request.onsuccess = (event) => {
      resolve(event.target.result)
    }

    request.onupgradeneeded = (event) => {
      const db = event.target.result

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains("responses")) {
        db.createObjectStore("responses", { keyPath: "id" })
      }

      if (!db.objectStoreNames.contains("history")) {
        db.createObjectStore("history", { keyPath: "id" })
      }
    }
  })
}

const saveResponsesToDB = async (responses) => {
  try {
    const db = await openDB()
    const transaction = db.transaction(["responses"], "readwrite")
    const store = transaction.objectStore("responses")

    // Clear existing data
    store.clear()

    // Add all responses
    for (const response of responses) {
      store.add(response)
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error("Error saving responses to IndexedDB:", error)
    // Fallback to localStorage if IndexedDB fails
    localStorage.setItem("chatResponses", JSON.stringify(responses))
  }
}

const getResponsesFromDB = async () => {
  try {
    const db = await openDB()
    const transaction = db.transaction(["responses"], "readonly")
    const store = transaction.objectStore("responses")
    const request = store.getAll()

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result)
      request.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error("Error getting responses from IndexedDB:", error)
    // Fallback to localStorage if IndexedDB fails
    const savedResponses = localStorage.getItem("chatResponses")
    return savedResponses ? JSON.parse(savedResponses) : []
  }
}

const saveHistoryToDB = async (history) => {
  try {
    const db = await openDB()
    const transaction = db.transaction(["history"], "readwrite")
    const store = transaction.objectStore("history")

    // Clear existing data
    store.clear()

    // Add all history items
    for (const item of history) {
      store.add(item)
    }

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error("Error saving history to IndexedDB:", error)
    // Fallback to localStorage if IndexedDB fails
    localStorage.setItem("chatHistory", JSON.stringify(history))
  }
}

const getHistoryFromDB = async () => {
  try {
    const db = await openDB()
    const transaction = db.transaction(["history"], "readonly")
    const store = transaction.objectStore("history")
    const request = store.getAll()

    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result)
      request.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error("Error getting history from IndexedDB:", error)
    // Fallback to localStorage if IndexedDB fails
    const savedHistory = localStorage.getItem("chatHistory")
    return savedHistory ? JSON.parse(savedHistory) : []
  }
}

const clearAllDataFromDB = async () => {
  try {
    const db = await openDB()
    const transaction = db.transaction(["responses", "history"], "readwrite")

    transaction.objectStore("responses").clear()
    transaction.objectStore("history").clear()

    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = (event) => reject(event.target.error)
    })
  } catch (error) {
    console.error("Error clearing data from IndexedDB:", error)
    // Fallback to localStorage if IndexedDB fails
    localStorage.removeItem("chatResponses")
    localStorage.removeItem("chatHistory")
  }
}

// Helper function to process HTML content with typewriter markers
const processTypewriterContent = (htmlContent) => {
  if (!htmlContent) return htmlContent

  // Replace all instances of the special markers with TypewriterRendererPlain components
  const regex = /<!-- TYPEWRITER_CONTENT_START -->([\s\S]*?)<!-- TYPEWRITER_CONTENT_END -->/g

  // We'll collect all the matches to process them later
  const matches = []
  let match
  while ((match = regex.exec(htmlContent)) !== null) {
    matches.push({
      fullMatch: match[0],
      content: match[1],
      index: match.index,
    })
  }

  // If no matches, return the original content
  if (matches.length === 0) return htmlContent

  // Replace each match with a unique ID that we can later replace with the actual component
  let processedHtml = htmlContent
  const placeholders = []

  matches.forEach((match, index) => {
    const placeholderId = `typewriter-placeholder-${Date.now()}-${index}`
    processedHtml = processedHtml.replace(match.fullMatch, `<div id="${placeholderId}" class="math-content"></div>`)
    placeholders.push({
      id: placeholderId,
      content: match.content,
    })
  })

  // Return both the processed HTML and the placeholders
  return {
    html: processedHtml,
    placeholders,
  }
}

function ChatBox() {

  const location = useLocation()
  const questionPrompt = location?.state?.questionPrompt || ""

  const [message, setMessage] = useState("")
  const [responses, setResponses] = useState([])
  const [pastedImages, setPastedImages] = useState([])
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [tempHistory, setTempHistory] = useState([])
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentModalImage, setCurrentModalImage] = useState(null)
  const messagesEndRef = useRef(null)
  const messageRefs = useRef({})

  // Store typewriter placeholders for each response
  const [typewriterPlaceholders, setTypewriterPlaceholders] = useState({})

  // New state for thinking mode
  const [thinkingMode, setThinkingMode] = useState("standard") // "standard", "advanced", or "deep"
  const [showDeep, setShowDeep] = useState(false);

  const [imageTexts, setImageTexts] = useState([])
  const [transcribingIndex, setTranscribingIndex] = useState(-1)
  const [transcriptionErrors, setTranscriptionErrors] = useState([])

  const isMobile = () => window.innerWidth / window.innerHeight < 1.6
  const [showHistory, setShowHistory] = useState(() => !isMobile())
  const [showClearConfirm, setShowClearConfirm] = useState(false)
  const [showIndent, setShowIndent] = useState(() => !isMobile())

  // Add state to track if messages are being loaded from history
  const [isLoadingFromHistory, setIsLoadingFromHistory] = useState(true)

  // Add a flag to track if we've already regenerated practice problems
  const [hasRegeneratedProblems, setHasRegeneratedProblems] = useState(false)

  // Add a ref to track which problems have been regenerated
  const regeneratedProblems = useRef(new Set())
  const [thinkingModeExpanded, setThinkingModeExpanded] = useState(false);
  const [showDescription, setShowDescription] = useState(false); // New state to control when to show the description

  const [isDragging, setIsDragging] = useState(false)


  const API_URL = "https://math-image-transcriber-steveszren.replit.app/api/convert-diagram"
  const MAX_IMAGES = 5

  // API endpoints for different thinking modes
  const API_ENDPOINTS = {
    standard: "/api/chat-stream",
    advanced: "/api/chat/advanced-stream",
    deep: "/api/chat/deep-stream",
  }

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [responses])

  // Now replace the existing useEffect for loading data with this one
  useEffect(() => {
    const loadData = async () => {
      setIsLoadingFromHistory(true)
      setHasRegeneratedProblems(false) // Reset regeneration flag
      regeneratedProblems.current.clear() // Clear the set of regenerated problems

      try {
        const savedResponses = await getResponsesFromDB()
        if (savedResponses && savedResponses.length > 0) {

          // Process any direct HTML content with typewriter markers
          savedResponses.forEach((response) => {
            if (response.isDirectHtml && response.response) {
              // If this is a practice problem with stored problem data, regenerate the HTML
              if (response.problemData && Array.isArray(response.problemData) && response.problemData.length > 0) {
                // We'll regenerate the HTML later after setting the state
              } else {
                // For other HTML content, just process typewriter sections
                const { processedContent } = processHtmlContent(response.response, response.id)
                response.response = processedContent
              }
            }
          })

          setResponses(savedResponses)
        }

        const savedHistory = await getHistoryFromDB()
        if (savedHistory && savedHistory.length > 0) {
          setTempHistory(savedHistory)
        }
      } catch (e) {
        console.error("Error loading data from IndexedDB:", e)

        // Fallback to localStorage
        const localResponses = localStorage.getItem("chatResponses")
        const localHistory = localStorage.getItem("chatHistory")

        if (localResponses) {
          try {
            const parsedResponses = JSON.parse(localResponses)

            // Process any direct HTML content with typewriter markers
            parsedResponses.forEach((response) => {
              if (response.isDirectHtml && response.response) {
                // If this is a practice problem with stored problem data, regenerate the HTML
                if (response.problemData && Array.isArray(response.problemData) && response.problemData.length > 0) {
                  // We'll regenerate the HTML later after setting the state
                } else {
                  // For other HTML content, just process typewriter sections
                  const { processedContent } = processHtmlContent(response.response, response.id)
                  response.response = processedContent
                }
              }
            })

            setResponses(parsedResponses)
          } catch (e) {
            console.error("Error parsing saved responses:", e)
          }
        }

        if (localHistory) {
          try {
            setTempHistory(JSON.parse(localHistory))
          } catch (e) {
            console.error("Error parsing saved history:", e)
          }
        }
      } finally {
        // Set loading from history to false after a longer delay to ensure content is processed
        setTimeout(() => {
          setIsLoadingFromHistory(false)
        }, 500) // Increased delay to ensure content is processed
      }
    }

    loadData()
  }, [])

  // Replace the useEffect for saving responses
  useEffect(() => {
    if (responses.length > 0) {
      saveResponsesToDB(responses)
    }
  }, [responses])

  // Replace the useEffect for saving history
  useEffect(() => {
    if (tempHistory.length > 0) {
      saveHistoryToDB(tempHistory)
    }
  }, [tempHistory])

  useEffect(() => {
    if (questionPrompt) {
      setMessage(questionPrompt)
      sendMessage()
    }
  }, [questionPrompt])

  useEffect(() => {
    const handleResize = () => {
      setShowHistory(!isMobile())
      setShowIndent(!isMobile())
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])


  useEffect(() => {
    const handleDocumentDragOver = (e) => {
      e.preventDefault()
    }

    const handleDocumentDrop = (e) => {
      e.preventDefault()
    }

    document.addEventListener('dragover', handleDocumentDragOver)
    document.addEventListener('drop', handleDocumentDrop)

    return () => {
      document.removeEventListener('dragover', handleDocumentDragOver)
      document.removeEventListener('drop', handleDocumentDrop)
    }
  }, [])


  // Initialize mermaid and process typewriter content after each response is added
  useEffect(() => {
    if (responses.length > 0 && !isLoadingFromHistory) {
      try {
        // Wait for the DOM to update
        setTimeout(() => {
          // Try to initialize all mermaid diagrams in the chat
          try {
            mermaid.contentLoaded()
          } catch (error) {
            console.error("Mermaid content loaded error:", error)
          }

          // Also try direct initialization
          try {
            mermaid.init(undefined, document.querySelectorAll(".mermaid"))
          } catch (error) {
            console.error("Mermaid initialization error:", error)
          }

          // Process typewriter placeholders
          Object.entries(typewriterPlaceholders).forEach(([responseId, placeholders]) => {
            placeholders.forEach((placeholder) => {
              const element = document.getElementById(placeholder.id)
              if (element) {
                // Create a new TypewriterRendererPlain component for this placeholder
                const typewriterContent = document.createElement("div")
                typewriterContent.className = "typewriter-content"
                typewriterContent.innerHTML = placeholder.content
                element.appendChild(typewriterContent)
              }
            })
          })
        }, 300)
      } catch (error) {
        console.error("Content rendering error:", error)
      }
    }
  }, [responses, isLoadingFromHistory, typewriterPlaceholders])

  // Replace the clearAllChat function
  const clearAllChat = async () => {
    setResponses([])
    setTempHistory([])
    setTypewriterPlaceholders({})
    setHasRegeneratedProblems(false)
    regeneratedProblems.current.clear()
    await clearAllDataFromDB()
    setShowClearConfirm(false)
  }

  const scrollToMessage = (messageId) => {
    if (messageRefs.current[messageId]) {
      messageRefs.current[messageId].scrollIntoView({
        behavior: "smooth",
        block: "start",
      })
    }
  }

  // Update the sendMessage function to include image data if present
  // Add this state for uploaded files
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [processedFileContents, setProcessedFileContents] = useState([])

  // Add these handler functions before the return statement

  // Handle files added to the FileUploadHandler
  const handleFilesAdded = async (newFiles, allFiles) => {
    setUploadedFiles(allFiles)

    // Process the new files
    const processed = await processFiles(newFiles.map((f) => f.file))
    setProcessedFileContents((prev) => [...prev, ...processed])
  }

  // Handle files removed from the FileUploadHandler
  const handleFileRemoved = (fileId, remainingFiles) => {
    setUploadedFiles(remainingFiles)

    // If fileId is null, all files were removed
    if (fileId === null) {
      setProcessedFileContents([])
      return
    }

    // Remove the processed content for the removed file
    const fileIndex = uploadedFiles.findIndex((f) => f.id === fileId)
    if (fileIndex !== -1) {
      setProcessedFileContents((prev) => prev.filter((_, i) => i !== fileIndex))
    }
  }

  // Update the sendMessage function to include file data
  const sendMessage = async () => {
    if (!message && imageTexts.length === 0 && processedFileContents.length === 0) return
    setErrorMessage(null)

    // Combine user message with all transcribed image texts and file contents
    let combinedMessage = message

    if (imageTexts.length > 0) {
      combinedMessage += "\n\nTranscribed from images:\n"
      imageTexts.forEach((text, index) => {
        combinedMessage += `\nImage ${index + 1}: ${text}\n`
      })
    }

    if (processedFileContents.length > 0) {
      combinedMessage += "\n\nFile contents:\n"
      processedFileContents.forEach((file, index) => {
        combinedMessage += `\nFile ${index + 1} (${file.name} - ${file.type}): ${file.content}\n`
      })
    }

    setLoading(true)
    try {
      // Get the last two rounds of conversation
      const lastTwoRounds = responses
        .slice(-2)
        .flatMap((item) => {
          // Only include messages that have valid content
          if (item.response) {
            return [
              { role: "user", content: item.query },
              { role: "assistant", content: item.response },
            ]
          } else if (item.toolType === "practice" || item.type === "practice") {
            // Skip practice problem messages in the conversation history
            return []
          } else {
            // For other messages with missing response, only include the user query
            return [{ role: "user", content: item.query }]
          }
        })
        .filter((message) => message.content) // Filter out any messages with null/undefined content

      // Add the new user message
      const fullConversation = [...lastTwoRounds, { role: "user", content: combinedMessage }]

      const requestBody = {
        conversation: fullConversation,
        thinkingMode: thinkingMode, // Include the thinking mode in the request
        image: pastedImages.length > 0 ? pastedImages[0] : null, // Include the first image if available
      }

      // Use the appropriate API endpoint based on the thinking mode
      const apiEndpoint = API_ENDPOINTS[thinkingMode]





      // --- create an empty chat bubble right away ---
      const messageId = `msg-${Date.now()}`;
      setResponses(prev => [
        ...prev,
        {
          id: messageId,
          query: message,        // user’s text
          response: '',          // will fill in as we stream
          thinkingMode,
          imageData: pastedImages,
          imageTexts,
          toolType: null,
          type: null,
          problemData: null,
        },
      ]);

      // --- open the SSE stream ---
      const res = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(requestBody),
      });

      if (!res.ok || !res.body) {
        throw new Error(`Stream error ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');

      let buffer = '';
      let assistantText = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // split on blank line (per SSE spec)
        const events = buffer.split(/\r?\n\r?\n/);
        buffer = events.pop() || '';        // keep partial chunk, never null

        for (const evt of events) {
          const trimmed = evt.trim();
          if (!trimmed.startsWith('data:')) continue;

          const payload = trimmed.replace(/^data:\s*/, '');

          if (payload === '[DONE]') {
            reader.cancel();
            break;
          }

          let chunk = '';
          try {
            const parsed = JSON.parse(payload);
            chunk =
              typeof parsed === 'string'
                ? parsed
                : (parsed.choices && parsed.choices[0].delta
                  ? parsed.choices[0].delta.content
                  : '');
          } catch (e) {
            // ignore malformed JSON fragments
            continue;
          }

          if (!chunk) continue;
          assistantText += chunk;

          // update the bubble with the partial text
          setResponses(prev =>
            prev.map(m =>
              m.id === messageId ? { ...m, response: assistantText } : m
            )
          );
        }
      }

      // --- add to temp history when finished ---
      setTempHistory(prev => [
        ...prev,
        {
          id: messageId,
          query: message,
          response: assistantText,
          thinkingMode,
          imageData: pastedImages,
          imageTexts,
          toolType: null,
          type: null,
          problemData: null,
        },
      ]);



      // Reset input fields
      setMessage("")
      setPastedImages([])
      setImageTexts([])
      setTranscriptionErrors([])
      setUploadedFiles([])
      setProcessedFileContents([])
    } catch (error) {
      console.error("API Error:", error)
      setErrorMessage("An unexpected error occurred. Please try again.")

      if (error.response) {
        console.error("Error Response:", error.response.data)
        setErrorMessage(error.response.data.message || "An unexpected error occurred on the server.")
      } else if (error.message) {
        setErrorMessage(
          error.message === "Network Error"
            ? "Network error: Unable to reach the server. Please try again later."
            : error.code === "ECONNABORTED"
              ? "Request Timeout: The request took too long to complete. Please try again later."
              : `Unexpected error: I'm not smart enough for this task. Try something easier`,
        )
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.")
      }
    } finally {
      setLoading(false)
    }
  }

  const transcribeImage = async (file, index) => {
    if (!file) return

    // Set the current transcribing index
    setTranscribingIndex(index)

    // Clear any previous error for this index
    setTranscriptionErrors((prev) => {
      const newErrors = [...prev]
      newErrors[index] = null
      return newErrors
    })

    const formData = new FormData()
    formData.append("image", file)

    try {
      const response = await fetch(API_URL, {
        method: "POST",
        body: formData,
      })

      const data = await response.json()
      if (data.success) {
        // Update the specific index in the imageTexts array
        setImageTexts((prev) => {
          const newTexts = [...prev]
          newTexts[index] = data.result
          return newTexts
        })
      } else {
        setTranscriptionErrors((prev) => {
          const newErrors = [...prev]
          newErrors[index] = data.error || "Failed to convert image"
          return newErrors
        })
      }
    } catch (err) {
      setTranscriptionErrors((prev) => {
        const newErrors = [...prev]
        newErrors[index] = "Error transcribing image"
        return newErrors
      })
      console.error("Transcription error:", err)
    } finally {
      setTranscribingIndex(-1)
    }
  }

  const handlePaste = (event) => {
    const items = event.clipboardData.items
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        // Check if we've reached the maximum number of images
        if (pastedImages.length >= MAX_IMAGES) {
          setErrorMessage(`Maximum of ${MAX_IMAGES} images allowed`)
          return
        }

        const file = item.getAsFile()
        const reader = new FileReader()

        reader.onload = (e) => {
          const newImageIndex = pastedImages.length
          setPastedImages((prev) => [...prev, e.target.result])

          // Transcribe the new image
          transcribeImage(file, newImageIndex)
        }

        reader.readAsDataURL(file)
        break
      }
    }
  }

const handleImageUpload = (event) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    // Check if these are image files for the existing image handling
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'))
    const otherFiles = Array.from(files).filter(file => !file.type.startsWith('image/'))

    // Handle image files with the existing logic
    if (imageFiles.length > 0) {
      // Check if adding these files would exceed the limit
      if (pastedImages.length + imageFiles.length > MAX_IMAGES) {
        setErrorMessage(`Maximum of ${MAX_IMAGES} images allowed`)
        return
      }

      // Process each image file
      imageFiles.forEach((file, fileIndex) => {
        const reader = new FileReader()
        reader.onload = (e) => {
          const newImageIndex = pastedImages.length + fileIndex
          setPastedImages((prev) => [...prev, e.target.result])

          // Transcribe the new image
          transcribeImage(file, newImageIndex)
        }
        reader.readAsDataURL(file)
      })
    }

    // Handle other files with the FileUploadHandler logic
    if (otherFiles.length > 0) {
      // Create file objects for the FileUploadHandler
      const newFiles = otherFiles.map((file) => ({
        id: `file-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        file: file,
        name: file.name,
        type: file.type || getFileTypeFromExtension(file.name),
        size: file.size,
        preview: getFilePreview(file),
      }))

      const updatedFiles = [...uploadedFiles, ...newFiles]
      setUploadedFiles(updatedFiles)

      // Process the files
      handleFilesAdded(newFiles, updatedFiles)
    }
  }

  useEffect(() => {
    // Delay the description appearance after the button expansion
    if (thinkingMode === 'deep' || thinkingMode === 'advanced') {
      const timer = setTimeout(() => {
        setShowDescription(true); // Show description after delay
      }, 800); // Wait for the expansion animation to complete before showing description

      return () => clearTimeout(timer); // Cleanup the timer on unmount or mode change
    }
  }, [thinkingMode]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const removeImage = (index) => {
    setPastedImages((prev) => prev.filter((_, i) => i !== index))
    setImageTexts((prev) => prev.filter((_, i) => i !== index))
    setTranscriptionErrors((prev) => prev.filter((_, i) => i !== index))
  }

  const removeAllImages = () => {
    setPastedImages([])
    setImageTexts([])
    setTranscriptionErrors([])
  }

  const openImageModal = (imageUrl) => {
    setCurrentModalImage(imageUrl)
    setIsModalOpen(true)
  }

  // Function to get the appropriate color for the thinking mode button
  const getThinkingModeColor = (mode) => {
    if (mode === thinkingMode) {
      // Active mode
      switch (mode) {
        case "standard":
          return "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md"
        case "advanced":
          return "bg-gradient-to-r from-purple-500 to-purple-600 text-white shadow-md"
        case "deep":
          return "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-md"
        default:
          return "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md"
      }
    } else {
      // Inactive mode
      return "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm transition-all"
    }
  }

  // Function to get the appropriate color for the thinking mode badge
  const getThinkingModeBadgeColor = (mode) => {
    switch (mode) {
      case "standard":
        return "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
      case "advanced":
        return "bg-gradient-to-r from-purple-500 to-purple-600 text-white"
      case "deep":
        return "bg-gradient-to-r from-red-500 to-red-600 text-white"
      default:
        return "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
    }
  }

  const handleModeClick = (mode) => {
    setThinkingMode(mode);
    setThinkingModeExpanded(true); // Toggle expansion
  };

  const getModeDescription = () => {
    switch (thinkingMode) {
      case "standard":
        return "Common Tasks";
      case "advanced":
        return "Deeper insights";
      case "deep":
        return "Advanced Reasoning";
      default:
        return "";
    }
  };

  // Replace the processHtmlContent function with this improved version
  const processHtmlContent = (content, responseId) => {
    if (!content) return { processedContent: content }

    // Process the content to extract typewriter sections
    const result = processTypewriterContent(content)

    if (result && result.placeholders && result.placeholders.length > 0) {
      // Store the placeholders for this response
      setTypewriterPlaceholders((prev) => ({
        ...prev,
        [responseId]: result.placeholders,
      }))

      return { processedContent: result.html }
    }

    return { processedContent: content }
  }

  // Add this new function after the processHtmlContent function

  // New function to add a direct response to the chatbox
  const handleEducationalToolResponse = useCallback(
    (config) => {
      // Log the received config to debug

      // Create a user prompt that describes what was generated
      let userPrompt = ""

      switch (config.type) {
        case "practice":
          userPrompt = `Generate ${config.difficulty} level ${config.topic || config.subject} practice problems.`
          break
        case "visualize":
          userPrompt = `Create a ${config.visualizationType} visualization for the concept of "${config.concept}".`
          break
        case "lesson":
          userPrompt = `Create a lesson plan for a ${config.duration}-minute ${config.subject} lesson on "${config.topic}" for ${config.gradeLevel}.`
          break
        default:
          userPrompt = "Generate educational content."
      }

      // Generate a unique ID for this message
      const messageId = `msg-${Date.now()}`

      // If this is a fallback request, handle it differently
      if (config.fallback) {
        // For fallback, we'll just create a regular chat message
        setMessage(userPrompt)
        setTimeout(() => sendMessage(), 10)
        return messageId
      }

      // Ensure consistent property naming - use toolType for all tools
      const toolType = config.type || "practice"

      // Validate problem data if this is a practice tool
      let validatedProblemData = null
      if (toolType === "practice" && config.problemData && Array.isArray(config.problemData)) {
        // Deep clone the data to avoid reference issues
        validatedProblemData = JSON.parse(JSON.stringify(config.problemData)).map((problem) => {
          // Ensure all required fields exist and are strings
          return {
            question: typeof problem.question === "string" ? problem.question : "Question not available",
            hint: typeof problem.hint === "string" ? problem.hint : "No hint available",
            answer: typeof problem.answer === "string" ? problem.answer : "Answer not available",
            explanation: typeof problem.explanation === "string" ? problem.explanation : "No explanation available",
          }
        })

      }

      // Create the response object with consistent property naming
      const responseObj = {
        id: messageId,
        query: userPrompt,
        toolType: toolType,
        type: toolType, // For backward compatibility
        thinkingMode: thinkingMode,
        problemData: validatedProblemData,
        subject: config.subject || null,
        topic: config.topic || null,
        difficulty: config.difficulty || null,
        response: config.response || null,
      }


      // Add to history
      setTempHistory((prev) => [...prev, responseObj])

      // Add to responses
      setResponses((prev) => [...prev, responseObj])

      // Scroll to the new message
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
      }, 100)

      return messageId
    },
    [thinkingMode, setMessage],
  )

  // Add a new function to regenerate practice problems from stored data
  const regeneratePracticeProblems = useCallback(() => {


    // If we've already regenerated problems, don't do it again
    if (hasRegeneratedProblems) {
      return
    }

    // Set the flag to indicate we've regenerated problems
    setHasRegeneratedProblems(true)

    // No need to manually regenerate HTML - the PracticeDisplay component will handle it
  }, [isLoadingFromHistory, hasRegeneratedProblems])

  // Add this function to format practice problems from stored data
  // Find the formatPracticeProblemsFromData function and update it to use React state for toggling

  // Update the formatPracticeProblemsFromData function to use a simpler approach

  // Replace the useEffect that adds the global script with this updated version
  useEffect(() => {
    // Create a script element for global functions
    const script = document.createElement("script")
    script.innerHTML = `
    // Global function to toggle hint visibility
    window.toggleHint = function(problemId) {
      console.log("Toggle hint for", problemId);
      const hintElement = document.getElementById('hint-' + problemId);
      if (hintElement) {
        hintElement.classList.toggle('hidden');
      }
    };
    
    // Global function to toggle solution visibility
    window.toggleSolution = function(problemId) {
      console.log("Toggle solution for", problemId);
      const solutionElement = document.getElementById('solution-' + problemId);
      if (solutionElement) {
        solutionElement.classList.toggle('hidden');
      }
    };
  `
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  // Remove the global script for button clicks since we're now using direct event listeners
  // Replace the useEffect that adds the global script with this simplified version
  useEffect(() => {
    // Create a script element for any other global functions we might need
    const script = document.createElement("script")
    script.innerHTML = `
    // Any other global functions can go here
  `
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  // Add a useEffect to regenerate practice problems after loading from history
  useEffect(() => {
    if (responses.length > 0 && isLoadingFromHistory === false && !hasRegeneratedProblems) {
      // This runs after history is loaded
      regeneratePracticeProblems()
    }
  }, [isLoadingFromHistory, responses, regeneratePracticeProblems, hasRegeneratedProblems])

  // Process typewriter content after rendering
  // Replace the useEffect that processes typewriter content with this improved version
  useEffect(() => {
    // Process typewriter placeholders for both fresh responses and loaded history
    if (Object.keys(typewriterPlaceholders).length > 0) {
      setTimeout(() => {
        Object.entries(typewriterPlaceholders).forEach(([responseId, placeholders]) => {
          placeholders.forEach((placeholder) => {
            const element = document.getElementById(placeholder.id)
            if (element) {
              // Use the existing TypeWriterResponsePlain component
              const typewriterContainer = document.createElement("div")
              typewriterContainer.className = "typewriter-container"
              element.innerHTML = "" // Clear any existing content
              element.appendChild(typewriterContainer)

              // Create a new instance of TypeWriterResponsePlain
              const typewriterContent = document.createElement("div")
              typewriterContent.className = "typewriter-content"
              typewriterContainer.appendChild(typewriterContent)

              // Import the TypeWriterResponsePlain component's functionality
              const content = placeholder.content

              // Use the existing typewriterEffectResponse function from TypeWriterResponsePlain
              import("./TypeWriterResponsePlain")
                .then((module) => {
                  const TypewriterRenderer = module.default

                  // Create a temporary container
                  const tempContainer = document.createElement("div")
                  tempContainer.style.display = "none"
                  document.body.appendChild(tempContainer)

                  // Render the TypewriterRenderer component into the temporary container
                  const root = ReactDOM.createRoot(tempContainer)
                  // Don't use typewriter effect when loading from history
                  root.render(<TypewriterRenderer content={content} useTypewriter={!isLoadingFromHistory} />)

                  // After a short delay to ensure rendering, move the content to the actual container
                  setTimeout(() => {
                    if (typewriterContent) {
                      typewriterContent.innerHTML = tempContainer.innerHTML
                      document.body.removeChild(tempContainer)
                    }
                  }, 100)
                })
                .catch((error) => {
                  console.error("Error importing TypeWriterResponsePlain:", error)
                  // Fallback to simple rendering without typewriter effect
                  typewriterContent.innerHTML = content
                })
            }
          })
        })
      }, 300)
    }
  }, [typewriterPlaceholders, isLoadingFromHistory])


  const fileInputRef = useRef(null)
  // Add this new useEffect to initialize practice problems after loading from history
  // Add this after the existing useEffect hooks
  useEffect(() => {
    if (!isLoadingFromHistory && responses.length > 0) {
      // Wait for the DOM to update
      setTimeout(() => {
        // Initialize practice problems
        // No need to call initializePracticeProblems() anymore as we're using React components

        // Process any direct HTML content
        responses.forEach((response) => {
          if (response.isDirectHtml) {
            // Find all buttons in practice problems and attach event listeners
            const mathContent = document.querySelectorAll(".math-content")
            mathContent.forEach((el) => {
              if (window.renderMathInElement) {
                window.renderMathInElement(el, {
                  delimiters: [
                    { left: "$$", right: "$$", display: true },
                    { left: "$", right: "$", display: false },
                  ],
                  throwOnError: false,
                })
              }
            })
          }
        })
      }, 500)
    }
  }, [isLoadingFromHistory, responses])

  // Modify the existing KaTeX useEffect to also handle practice problems
  // Replace the existing useEffect for processing math content with this updated version
  useEffect(() => {
    if (responses.length > 0) {
      // Wait for KaTeX to be loaded
      const checkKatex = setInterval(() => {
        if (window.renderMathInElement) {
          clearInterval(checkKatex)

          setTimeout(() => {
            try {
              // Find all elements with math content
              document.querySelectorAll(".math-content").forEach((el) => {
                window.renderMathInElement(el, {
                  delimiters: [
                    { left: "$$", right: "$$", display: true },
                    { left: "$", right: "$", display: false },
                  ],
                  throwOnError: false,
                })
              })

              // Initialize practice problems
              // No need to call initializePracticeProblems() anymore as we're using React components
            } catch (error) {
              console.error("Error rendering math in HTML content:", error)
            }
          }, 300)
        }
      }, 100)

      // Clear interval after 5 seconds to prevent infinite checking
      setTimeout(() => clearInterval(checkKatex), 5000)
    }
  }, [responses])

  // Add a script to initialize KaTeX
  useEffect(() => {
    // Create a script element for KaTeX
    const script = document.createElement("script")
    script.innerHTML = `
      document.addEventListener('DOMContentLoaded', function() {
        // Initialize KaTeX auto-render
        if (window.renderMathInElement) {
          document.querySelectorAll('.math-content').forEach(function(el) {
            window.renderMathInElement(el, {
              delimiters: [
                { left: "$$", right: "$$", display: true },
                { left: "$", right: "$", display: false }
              ],
              throwOnError: false
            });
          });
        }
      });
    `
    document.head.appendChild(script)

    return () => {
      document.head.removeChild(script)
    }
  }, [])

  // Add this new useEffect to ensure we initialize practice problems when the page loads
  useEffect(() => {
    // Initialize practice problems when the component mounts
    const timer = setTimeout(() => {
      // No need to call initializePracticeProblems() anymore as we're using React components

      // Also try to find and fix any existing practice problems
      document.querySelectorAll(".practice-problem").forEach((problem) => {
        const problemId = problem.id
        if (!problemId) return

        // Find hint and solution elements
        const hintElement = problem.querySelector(".problem-hint")
        const solutionElement = problem.querySelector(".problem-solution")

        // Make sure they have the correct IDs
        if (hintElement && !hintElement.id) {
          hintElement.id = `hint-${problemId}`
        }

        if (solutionElement && !solutionElement.id) {
          solutionElement.id = `solution-${problemId}`
        }

        // Make sure they have both class and style for hiding
        if (hintElement) {
          if (hintElement.classList.contains("hidden")) {
            hintElement.style.display = "none"
          }
        }

        if (solutionElement) {
          if (solutionElement.classList.contains("hidden")) {
            solutionElement.style.display = "none"
          }
        }
      })
    }, 1000) // Delay to ensure DOM is fully loaded

    return () => clearTimeout(timer)
  }, [])

  // Add this MutationObserver to detect when new practice problems are added to the DOM
  useEffect(() => {
    // Create a MutationObserver to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      let shouldInitialize = false

      // Check if any practice problems were added
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Element node
              if (node.querySelector(".practice-problem") || node.classList?.contains("practice-problem")) {
                shouldInitialize = true
              }
            }
          })
        }
      })


    })

    // Start observing the document body
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [])

  // Add this new useEffect to ensure we initialize practice problems when the page loads
  useEffect(() => {
    // Initialize practice problems when the component mounts
    const timer = setTimeout(() => {
      // No need to call initializePracticeProblems() anymore as we're using React components
    }, 1000) // Delay to ensure DOM is fully loaded

    return () => clearTimeout(timer)
  }, [])

  // Add this MutationObserver to detect when new practice problems are added to the DOM
  useEffect(() => {
    // Create a MutationObserver to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      let shouldInitialize = false

      // Check if any practice problems were added
      mutations.forEach((mutation) => {
        if (mutation.type === "childList") {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) {
              // Element node
              if (node.querySelector(".practice-problem") || node.classList?.contains("practice-problem")) {
                shouldInitialize = true
              }
            }
          })
        }
      })


    })

    // Start observing the document body
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    })

    return () => observer.disconnect()
  }, [])

  return (
    <>
      <Helmet>
        {/* 1. CMP */}
      <script
        id="usercentrics-cmp"
        src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
        data-settings-id="R7Xi1xeFRKhrKk"
        async
      />
      {/* 2. gtag */}
      <script
        async
        src="https://www.googletagmanager.com/gtag/js?id=AW-17303305286"
      />
      <script>{`
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', 'AW-17303305286');
      `}</script>
      {/* 3a. GTM head */}
      <script>{`
        (function(w,d,s,l,i){
          w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
          var f=d.getElementsByTagName(s)[0], j=d.createElement(s), dl=l!='dataLayer'?'&l='+l:'';
          j.async=true; j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
          f.parentNode.insertBefore(j,f);
        })(window,document,'script','dataLayer','GTM-MBKBFC9M');
      `}</script>
        <title>AI Chatbox - Get Instant Math Help</title>
        <meta
          name="description"
          content="Use our AI-powered chatbox to get instant math solutions, step-by-step explanations, and more."
        />
        <meta name="keywords" content="Math AI, Chatbot, Math solver, Step-by-step solutions, AI tutor" />
        <meta property="og:title" content="AI Chatbox - Solve Math Instantly" />
        <meta property="og:description" content="Get AI-generated step-by-step math solutions in our chatbox." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <script src="https://cdn.jsdelivr.net/npm/katex@0.16.0/dist/katex.min.js"></script>
        <script src="https://cdn.jsdelivr.net/npm/katex@0.16.0/dist/contrib/auto-render.min.js"></script>
        <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/katex@0.16.0/dist/katex.min.css" />
      </Helmet>
      <div className="flex min-h-screen max-h-full bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 text-gray-900 dark:text-gray-100">
        {/* Left Sidebar - Chat History */}
        {showHistory && (
          <div className="fixed top-0 left-0 w-64 h-full bg-white/80 dark:bg-gray-800/50 backdrop-blur-sm border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto shadow-lg transition-all duration-300 ease-in-out animate-slide-in-left z-0">
            {/* Header with Close Button */}
            <div className="flex top-16 justify-between items-center mb-4 relative">
              <h2 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent -mt-2">
                History
              </h2>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowClearConfirm(true)}
                  className="bg-gray-900 text-amber-400 hover:bg-red-600 transition duration-300 px-4 py-2 rounded-full"
                  title="Clear all history"
                >
                  <FaTrash size={16} />
                </button>

                <button
                  onClick={() => setShowHistory(false)}
                  className="bg-gray-900 text-red-500 hover:text-red-600 transition duration-300 transform hover:rotate-90 px-4 py-2 rounded-full"
                  title="Close History"
                >
                  <FaTimes size={16} />
                </button>
              </div>
            </div>
            <hr className="border-gray-300" />
            {/* History Items */}
            {tempHistory.length > 0 ? (
              tempHistory.map((item, index) => (
                <div
                  key={index}
                  className="relative top-16 p-3 mb-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750"
                  onClick={() => scrollToMessage(item.id)}
                >
                  <p className="text-sm font-medium line-clamp-3">{item.query}</p>
                  {/* Show thinking mode badge */}
                  {item.thinkingMode && (
                    <span
                      className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full ${getThinkingModeBadgeColor(
                        item.thinkingMode,
                      )}`}
                    >
                      {item.thinkingMode.charAt(0).toUpperCase() + item.thinkingMode.slice(1)}
                    </span>
                  )}
                  {/* Show tool type badge if it exists */}
                  {item.toolType && (
                    <span className="inline-block mt-2 ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800">
                      {item.toolType.charAt(0).toUpperCase() + item.toolType.slice(1)}
                    </span>
                  )}
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-40 text-gray-400 dark:text-gray-500 space-y-2">
                <br></br>
                <br></br>
                <br></br>

                <FaHistory className="text-3xl opacity-50" />
                <p className="text-sm">No history yet</p>
              </div>
            )}
          </div>
        )}

        {/* Chat Main Section */}
        <div className={`flex flex-col flex-1 h-full overflow-hidden ${showHistory ? "ml-64" : ""}`}>
          {/* Placeholder Message Before Chat Starts */}
          {responses.length === 0 && (
            <div className="flex flex-col items-center justify-center flex-grow text-gray-700 dark:text-gray-300 space-y-8 p-6 animate-fade-in">
              {/* Welcome Header */}
              <div className="text-center space-y-4 max-w-2xl">
                <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent animate-gradient">
                  Welcome to <span className="text-gray-900 dark:text-white">AIAT</span>
                </h1>
                <p className="text-xl text-gray-600 dark:text-gray-400">
                  Your AI-powered assistant for exploring mathematics, solving problems, and visualizing concepts.
                </p>
                <p className="text-lg text-gray-500 dark:text-gray-500 italic">
                  "Start a conversation and let AIAT guide your mathematical journey."
                </p>
              </div>

              {/* Quick Math Prompts */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-8 w-full max-w-4xl">
                {[
                  "Explain the Pythagorean theorem",
                  "Differentiate sin(x) step by step",
                  "Solve this quadratic equation: x^2 - 5x + 6 = 0",
                  "What is the Taylor series expansion of e^x?",
                  "Give me a question on Vieta's formula",
                  "Find the integral of 1/(1+x^2)",
                  "Difference between sequence and series",
                  "Generate a challenging calculus problem",
                ].map((prompt, index) => (
                  <button
                    key={prompt}
                    onClick={() => {
                      setMessage(prompt)
                      setTimeout(() => sendMessage(), 10)
                    }}
                    className="px-4 py-3 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm rounded-xl shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-700 transition-all duration-300 transform hover:scale-105 hover:bg-gray-50 dark:hover:bg-gray-750"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Chat Messages */}
          <div className="flex-grow overflow-y-auto p-6 pb-32 h-full">
            {responses.map((item, index) => (
              <div
                key={index}
                className="mb-8 flex flex-col space-y-4 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                ref={(el) => (messageRefs.current[item.id] = el)}
                id={item.id}
              >
                {/* User Message */}
                <div className="flex justify-end">
                  <div className="bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-750 p-4 rounded-2xl max-w-sm shadow-sm hover:shadow-md transition-all duration-200 border border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center mb-2">
                      <p className="font-semibold text-sm text-gray-700 dark:text-gray-300">You</p>
                    </div>

                    {/* Conditionally render user message */}
                    {item.query && (
                      <div
                        className="prose dark:prose-invert text-gray-800 dark:text-gray-200 p-3 rounded-lg"
                        dangerouslySetInnerHTML={{ __html: item.query }}
                      />
                    )}

                    {/* Show the images in the user message if they exist */}
                    {item.imageData && item.imageData.length > 0 && (
                      <div className="mt-3 space-y-3">
                        {item.imageData.map((img, imgIndex) => (
                          <div key={imgIndex} className="relative">
                            <img
                              src={img || "/placeholder.svg"}
                              alt={`User uploaded ${imgIndex + 1}`}
                              className="rounded-lg max-w-full shadow-sm cursor-pointer hover:opacity-90 transition-all duration-200 border border-gray-200 dark:border-gray-700"
                              onClick={() => openImageModal(img)}
                            />
                            {item.imageTexts && item.imageTexts[imgIndex] && (
                              <div className="mt-2 p-2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-md text-gray-800 dark:text-gray-200 text-xs border border-gray-200 dark:border-gray-700">
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* AI Response */}
                <div className="flex justify-start w-full">
                  <motion.div
                    layout            // ★ tells Framer to animate size & position changes
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{
                      opacity: { duration: 0.5, ease: 'easeInOut' },  // fade-in
                      layout: { duration: 0, ease: 'easeOut' }       // height transition
                    }} className="bg-white dark:bg-gray-300 text-gray-800 dark:text-gray-100 p-4 rounded-2xl inline-block min-w-full shadow-md hover:shadow-lg transition-all duration-200 border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <div className="flex items-center space-x-2">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                          AI
                        </div>
                        <p className="font-semibold text-sm text-gray-700 dark:text-gray-600">AI Response</p>
                      </div>

                      {/* Show thinking mode badge */}
                      {item.thinkingMode && (
                        <span
                          className={`inline-block text-xs px-3 py-1 rounded-full shadow-sm ${getThinkingModeBadgeColor(
                            item.thinkingMode,
                          )}`}
                        >
                          {item.thinkingMode.charAt(0).toUpperCase() + item.thinkingMode.slice(1)}
                        </span>
                      )}

                      {/* Show tool type badge if it exists */}
                      {item.toolType && (
                        <span className="inline-block text-xs px-3 py-1 rounded-full shadow-sm bg-green-100 text-green-800 ml-2">
                          {item.toolType.charAt(0).toUpperCase() + item.toolType.slice(1)}
                        </span>
                      )}
                    </div>
                    {showIndent && (
                      <div className="pl-10">
                        {/* Improve the condition to check for practice problems */}
                        {(item.toolType === "practice" || item.type === "practice") &&
                          item.problemData &&
                          Array.isArray(item.problemData) &&
                          item.problemData.length > 0 &&
                          item.problemData.some((problem) => problem.question) ? (
                          <PracticeDisplay
                            problems={item.problemData}
                            subject={item.subject || "General"}
                            topic={item.topic || ""}
                            level={item.level || ""}
                            difficulty={item.difficulty || "Intermediate"}
                            useTypewriter={!isLoadingFromHistory && index === responses.length - 1}
                          />
                        ) : item.isDirectHtml ? (
                          <div className="math-content" dangerouslySetInnerHTML={{ __html: item.response || "" }} />
                        ) : (
                          <AIResponse
                            content={item.response || ""}
                            useTypewriter={false && index === responses.length - 1}
                          />
                        )}
                      </div>
                    )}
                    {!showIndent && (
                      <div className="pl-0">
                        {/* Duplicate the same improved condition here */}
                        {(item.toolType === "practice" || item.type === "practice") &&
                          item.problemData &&
                          Array.isArray(item.problemData) &&
                          item.problemData.length > 0 &&
                          item.problemData.some((problem) => problem.question) ? (
                          <PracticeDisplay
                            problems={item.problemData}
                            subject={item.subject || "General"}
                            topic={item.topic || ""}
                            level={item.level || ""}
                            difficulty={item.difficulty || "Intermediate"}
                            useTypewriter={!isLoadingFromHistory && index === responses.length - 1}
                          />
                        ) : item.isDirectHtml ? (
                          <div className="math-content" dangerouslySetInnerHTML={{ __html: item.response || "" }} />
                        ) : (
                          <AIResponse
                            content={item.response || ""}
                            useTypewriter={!isLoadingFromHistory && index === responses.length - 1}
                          />
                        )}
                      </div>
                    )}
                  </motion.div>
                </div>

                {/* Response Image (if exists) */}
                {item.responseImage && (
                  <div className="flex justify-start pl-10">
                    <img
                      src={item.responseImage || "/placeholder.svg"}
                      alt="AI Response"
                      className="rounded-lg max-w-sm shadow-md border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:shadow-lg"
                    />
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
          {/* Educational Toolbar - Now positioned on the right side */}
          <EducationalToolbar onSubmit={handleEducationalToolResponse} />
          {/* Message Input at Bottom */}
          <div
            className={`fixed bottom-0 justify-start ${showHistory ? "left-64" : "left-0"} right-0 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md border-t border-gray-200 dark:border-gray-700 p-4 flex flex-col space-y-3 animate-slide-in-bottom`}
          >
            {/* File Upload Handler */}
              <FileUploadHandler
                onFilesAdded={handleFilesAdded}
                onFileRemoved={handleFileRemoved}
                maxFiles={5}
                acceptedFileTypes="image/*,application/pdf,text/*,application/vnd.openxmlformats-officedocument.*,application/vnd.ms-*"
                fileInputRef={fileInputRef}
              />
              {/* Uploaded Files Display - Show above textarea when files are present */}
              {uploadedFiles.length > 0 && (
                <div className="mb-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Uploaded Files ({uploadedFiles.length})
                    </h3>
                    <button
                      onClick={() => handleFileRemoved(null, [])}
                      className="text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors px-2 py-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      Remove All
                    </button>
                  </div>

                  <div className="h-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {uploadedFiles.map((file) => (
                      <div
                        key={file.id}
                        className="h-16 flex items-center justify-between p-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center overflow-hidden flex-1">
                          {file.preview ? (
                            <img
                              src={file.preview || "/placeholder.svg"}
                              alt={file.name}
                              className="h-10 w-10 object-cover rounded mr-3 border border-gray-200 dark:border-gray-600"
                            />
                          ) : (
                            <div className="h-10 w-10 flex items-center justify-center rounded mr-3 bg-gray-100 dark:bg-gray-600">
                              {getFileIcon(file.type, file.name)}
                            </div>
                          )}
                          <div className="overflow-hidden flex-1">
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate" title={file.name}>
                              {file.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleFileRemoved(file.id, uploadedFiles.filter(f => f.id !== file.id))}
                          className="ml-2 p-1.5 text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 rounded-full hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                          title="Remove file"
                        >
                          <FaTimes className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            {/* Thinking Mode Selector */}
            <div className="flex items-center space-x-2 ml-14">
              <div className="flex items-center space-x-1">
                <FaBrain className="text-gray-500 dark:text-gray-400" />
                <span className="text-xs text-gray-500 dark:text-gray-400">Thinking Mode:</span>
              </div>
              <div className="flex items-center space-x-2">
                {/* Standard Button */}
                <motion.button
                  onClick={() => handleModeClick('standard')}
                  className={`px-3 py-1 text-sm rounded-full transition-all duration-300 ${getThinkingModeColor('standard')}`}
                  initial={{ width: '120px', height: '24px', opacity: 1 }}
                  animate={{ width: thinkingMode === 'standard' ? '260px' : '80px', opacity: thinkingMode === 'standard' ? 1 : 0.7 }}
                  transition={{ duration: 0.5 }}
                  style={{ overflow: 'hidden' }}
                >
                  General Questions
                  {thinkingMode === 'standard' && showDescription && (
                    <motion.span
                      className="ml-2 text-xs"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.8 }}
                    >{getModeDescription()}</motion.span>
                  )}
                </motion.button>

                {/* Advanced Button */}
                <motion.button
                  onClick={() => handleModeClick('advanced')}
                  className={`px-3 py-1 text-sm rounded-full transition-all duration-300 ${getThinkingModeColor('advanced')}`}
                  initial={{ width: '120px', height: '24px', opacity: 1 }}
                  animate={{ width: thinkingMode === 'advanced' ? '260px' : '80px', opacity: thinkingMode === 'advanced' ? 1 : 0.7 }}
                  transition={{ duration: 0.5 }}
                  style={{ overflow: 'hidden' }}
                >
                  Scientific
                  {thinkingMode === 'advanced' && showDescription && (
                    <motion.span
                      className="ml-2 text-xs"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.8 }}
                    >{getModeDescription()}</motion.span>
                  )}
                </motion.button>

                {/* Deep Toggle */}
                <button
                  onClick={() => setShowDeep(prev => !prev)}
                  className="px-2 py-1 text-xs rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:outline-none transition-transform duration-200"
                >
                  <FaPlus className={showDeep ? 'transform rotate-45' : ''} />
                </button>

                <AnimatePresence>
                  {showDeep && (
                    <motion.button
                      onClick={() => handleModeClick('deep')}
                      className={`px-3 py-1 text-xs rounded-full transition-all duration-300 ${getThinkingModeColor('deep')}`}
                      initial={{ width: '60px', height: '24px', opacity: 0 }}
                      animate={{ width: thinkingMode === 'deep' ? '160px' : '80px', opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.5 }}
                      style={{ overflow: 'hidden' }}
                    >
                      Deep
                      {thinkingMode === 'deep' && showDescription && (
                        <motion.span
                          className="ml-2 text-xs"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3, delay: 0.8 }}
                        >{getModeDescription()}</motion.span>
                      )}
                    </motion.button>
                  )}
                </AnimatePresence>
              </div>
            </div>


            {/* Input Area */}
            <div className="flex items-center space-x-4">
              {/* Upload Image Button */}
              <label className="cursor-pointer flex items-center justify-center bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full p-3 hover:bg-gray-50 dark:hover:bg-gray-650 transition-all duration-200 shadow-sm hover:shadow-md">
                <FaImage className="text-gray-600 dark:text-gray-300 text-lg" />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                  multiple
                  disabled={pastedImages.length >= MAX_IMAGES}
                />
              </label>

              <div className={`relative min-w-[65vw] flex-1 rounded-xl ${loading ? "sending-border" : ""}`}>
                {/* File Drop Overlay - Always capture drag events */}
                <div
                  className={`absolute inset-0 z-10 rounded-xl transition-all duration-200 ${isDragging
                      ? "bg-blue-50 bg-opacity-95 border-2 border-dashed border-blue-500"
                      : "pointer-events-none"
                    }`}
                  onDragEnter={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsDragging(true)
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsDragging(true)
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    // Only set dragging to false if we're leaving the container entirely
                    const rect = e.currentTarget.getBoundingClientRect()
                    const x = e.clientX
                    const y = e.clientY
                    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                      setIsDragging(false)
                    }
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsDragging(false)

                    const droppedFiles = e.dataTransfer.files
                    if (droppedFiles && droppedFiles.length > 0) {
                      handleImageUpload({ target: { files: droppedFiles } })
                    }
                  }}
                >
                  {isDragging && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="bg-white p-6 rounded-lg shadow-lg text-center border-2 border-blue-500">
                        <Upload className="h-12 w-12 text-blue-500 mx-auto mb-3" />
                        <p className="text-lg font-medium text-gray-700 mb-1">Drop files here to upload</p>
                        <p className="text-sm text-gray-500">Supports images, PDFs, documents, and more</p>
                      </div>
                    </div>
                  )}
                </div>

                <textarea
                  className="w-full p-4 bg-white dark:bg-black border border-gray-300 dark:border-gray-600 rounded-xl resize-none text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-400 dark:focus:ring-blue-500 focus:outline-none shadow-sm transition-all duration-200"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onPaste={handlePaste}
                  onKeyDown={handleKeyDown}
                  onDragEnter={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsDragging(true)
                  }}
                  onDragOver={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsDragging(true)
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                  }}
                  onDrop={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setIsDragging(false)

                    const droppedFiles = e.dataTransfer.files
                    if (droppedFiles && droppedFiles.length > 0) {
                      handleImageUpload({ target: { files: droppedFiles } })
                    }
                  }}
                  rows="2"
                  placeholder="Type your message or drop files here..."
                />
                {loading && (
                  <div className="absolute inset-0 border-4 border-transparent rounded-xl sending-border-effect"></div>
                )}
              </div>

              {/* Send Button */}
              <button
                onClick={sendMessage}
                disabled={loading || transcribingIndex !== -1}
                className={`bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-5 py-3 rounded-full shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-50 transform hover:scale-105 active:scale-95 flex items-center justify-center space-x-2 min-w-[100px]`}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Sending</span>
                  </div>
                ) : transcribingIndex !== -1 ? (
                  <div className="flex items-center space-x-2">
                    <svg
                      className="animate-spin h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    <span>Processing</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <FaPaperPlane className="text-sm" />
                    <span>Send</span>
                  </div>
                )}
              </button>
            </div>
          </div>
          {/* Show History Button */}
          {!showHistory && (
            <button
              onClick={() => setShowHistory(true)}
              className="fixed bottom-40 left-5 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-full shadow-md hover:shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-300 animate-pulse-slow"
            >
              <FaHistory />
              Show History
            </button>
          )}
          {/* Pasted Images Preview with Transcription Status */}
          {pastedImages.length > 0 && (
            <div className="fixed bottom-56 left-4 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-4 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 max-w-[calc(100vw-8rem)] md:max-w-[800px] animate-slide-in-bottom z-20">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-sm text-gray-700 dark:text-gray-300">
                  Uploaded Images ({pastedImages.length}/{MAX_IMAGES})
                </h3>
                <button
                  onClick={removeAllImages}
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-200"
                >
                  <FaClose />
                </button>
              </div>

              <div className="flex flex-wrap gap-3 max-h-[300px] overflow-y-auto p-1">
                {pastedImages.map((img, index) => (
                  <div
                    key={index}
                    className="relative border border-gray-200 dark:border-gray-700 rounded-lg p-2 w-[calc(33.333%-0.75rem)] max-w-[180px] flex-grow bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm transition-all duration-200 hover:shadow-md"
                  >
                    <div className="aspect-video relative overflow-hidden rounded-lg bg-gray-50 dark:bg-gray-900">
                      <img
                        src={img || "/placeholder.svg"}
                        alt={`Pasted ${index + 1}`}
                        className="w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform duration-200"
                        onClick={() => openImageModal(img)}
                      />

                      {/* Remove single image button */}
                      <button
                        onClick={() => removeImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600 transition-colors duration-200"
                      >
                        <FaClose size={12} />
                      </button>
                    </div>

                    {/* Transcription Status */}
                    <div className="mt-2">
                      {transcribingIndex === index ? (
                        <div className="text-blue-600 dark:text-blue-400 text-sm flex items-center">
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600 dark:text-blue-400"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Transcribing...
                        </div>
                      ) : transcriptionErrors[index] ? (
                        <div className="text-red-600 dark:text-red-400 text-sm">
                          Error: {transcriptionErrors[index]}
                        </div>
                      ) : imageTexts[index] ? (
                        <div className="text-green-600 dark:text-green-400 text-sm">
                          ✓ Transcribed
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Modal for Enlarged Image */}
          {isModalOpen && (
            <div
              className="fixed inset-0 bg-black bg-opacity-80 backdrop-blur-sm flex justify-center items-center z-50 animate-fade-in"
              onClick={() => setIsModalOpen(false)}
            >
              <div className="relative transform transition-all duration-300 scale-100">
                <img
                  src={currentModalImage || "/placeholder.svg"}
                  alt="Large Image"
                  className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-2xl border border-gray-700"
                />
                <button
                  className="absolute top-3 right-3 bg-white dark:bg-gray-800 p-2 rounded-full shadow-md hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors duration-200"
                  onClick={() => setIsModalOpen(false)}
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          {/* Error Message */}
          {errorMessage && (
            <div className="fixed bottom-28 left-4 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 border border-red-400 dark:border-red-800 p-3 rounded-lg shadow-md backdrop-blur-sm animate-slide-in-bottom">
              <strong>Error:</strong> {errorMessage}
            </div>
          )}
          {showClearConfirm && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex justify-center items-center z-50 animate-fade-in">
              <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-2xl max-w-md w-full mx-4">
                <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 mb-4">Clear All Chat History?</h3>
                <p className="text-gray-700 dark:text-gray-300 mb-6">
                  This will permanently delete all your chat history and messages. This action cannot be undone.
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowClearConfirm(false)}
                    className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={clearAllChat}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Clear All
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* New Chat Button - Only show when there are messages */}
          {responses.length > 0 && (
            <button
              onClick={() => setShowClearConfirm(true)}
              className="fixed top-4 right-4 flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-full shadow-md hover:shadow-lg hover:from-red-600 hover:to-red-700 transition-all duration-300"
            >
              <FaTrash className="text-sm" />
              <span>Clear Chat</span>
            </button>
          )}
        </div>
      </div>

    </>
  )
}

// Helper function to get file icon (move this from FileUploadHandler if not already present)
const getFileIcon = (fileType, fileName) => {
  const extension = fileName.split(".").pop().toLowerCase()

  if (fileType.startsWith("image/")) {
    return <FaImage className="h-5 w-5 text-blue-500" />
  } else if (fileType.startsWith("application/pdf") || extension === "pdf") {
    return <FaImage className="h-5 w-5 text-red-500" /> // You can replace with a PDF icon
  } else if (
    fileType.includes("spreadsheet") ||
    fileType.includes("excel") ||
    extension === "xlsx" ||
    extension === "xls"
  ) {
    return <FaImage className="h-5 w-5 text-green-500" /> // You can replace with a spreadsheet icon
  } else if (
    fileType.includes("document") ||
    fileType.includes("word") ||
    extension === "docx" ||
    extension === "doc"
  ) {
    return <FaImage className="h-5 w-5 text-blue-700" /> // You can replace with a document icon
  } else if (fileType.startsWith("text/")) {
    return <FaImage className="h-5 w-5 text-gray-500" /> // You can replace with a text icon
  } else {
    return <FaImage className="h-5 w-5 text-gray-500" />
  }
}

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes"
  const k = 1024
  const sizes = ["Bytes", "KB", "MB", "GB"]
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
}

const getFileTypeFromExtension = (filename) => {
  const ext = filename.split(".").pop().toLowerCase()
  const mimeTypes = {
    pdf: "application/pdf",
    doc: "application/msword",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    xls: "application/vnd.ms-excel",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    txt: "text/plain",
    csv: "text/csv",
  }
  return mimeTypes[ext] || "application/octet-stream"
}

const getFilePreview = (file) => {
  if (file.type.startsWith("image/")) {
    return URL.createObjectURL(file)
  }
  return null
}

function Upload(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
      <polyline points="17 8 12 3 7 8"></polyline>
      <line x1="12" y1="3" x2="12" y2="15"></line>
    </svg>
  )
}

export default ChatBox
